import { useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import {
  useTheme,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from 'state/axios';


const schema = yup.object({
  newPassword: yup.string().min(8, 'Minimum 8 characters').required('Required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Required'),
});


const ChangePassword = ({ userId }) => {
  const theme = useTheme();
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const formData = new FormData();
      formData.append('password', values.newPassword);
      await api.put(`/auth/update/${userId}`, formData);
      toast.success('Password updated successfully.');
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password.');
    }
  };


  return (
    <Box
      component="fieldset"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography component="legend" variant="subtitle1">
        Change Password
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Choose a strong password of at least 8 characters.
      </Typography>

      <Formik
        initialValues={{ newPassword: '', confirmPassword: '' }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ 
          values, 
          errors, 
          touched, 
          handleChange, 
          handleBlur, 
          handleSubmit: submit, 
          isSubmitting,
        }) => (
          <form onSubmit={submit}>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="newPassword"
                label="New password"
                type={showNew ? 'text' : 'password'}
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.newPassword && Boolean(errors.newPassword)}
                helperText={touched.newPassword && errors.newPassword}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disableRipple
                          size="small"
                          onClick={() => setShowNew((p) => !p)}
                          sx={{ 
                            bgcolor: 'transparent', 
                            '&:hover': { bgcolor: 'transparent' } 
                          }}
                        >
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                name="confirmPassword"
                label="Confirm new password"
                type={showConfirm ? 'text' : 'password'}
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disableRipple
                          size="small"
                          onClick={() => setShowConfirm((p) => !p)}
                          sx={{ 
                            bgcolor: 'transparent', 
                            '&:hover': { bgcolor: 'transparent' } 
                          }}
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
              >
                {isSubmitting ? 'Saving…' : 'Update'}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ChangePassword;
